import React, { useEffect, useCallback, useMemo } from "react";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import useGenericFilter from "../../../../hooks/filters/useGenericFilter";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import useAction from "../../../../hooks/useAction";
import { entities } from "../../../../constants/entities";
import useLoader from "../../../../hooks/useLoader";
import {
  faEdit,
  faLayerGroup,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import ListHeader from "../../../../components/ListHeader";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import TableWithInactiveFilter from "../../../../components/table/TableWithInactiveFilter";
import { inputsFiltersTypes } from "./filters/filters-types";
import FilterCollapse from "../../../../components/FilterCollapse";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const TypePage = () => {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      is_kit: [],
      deleted_at: false,
    },
    "almox_types",
  );

  // --- Lógica de busca de tipos com TanStack Query ---
  const {
    data: typesData,
    isLoading,
    isFetching,
    isError: isErrorTypes,
    error: typesError,
  } = useQueryWithLoader({
    queryKey: ["almox_types", { filters, pagination }],
    queryFn: async () => {
      const transformedFilters = transformFiltersForUrl(filters);
      const params = {
        filters: transformedFilters,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.types.get, params);
      return response.result;
    },
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        types: data.data.map((type) => ({
          id: type.id,
          name: type.name,
          is_kit: type.is_kit ? "Sim" : "Não",
          is_in_product: type.is_in_product,
          isDeleted: !!type.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["almox_types", { filters, pagination }],
    });
  }, [queryClient, filters, pagination]);

  // Extrai tipos e totalPages dos dados transformados pelo useQuery
  const { types = [], totalPages = 1 } = typesData || {};

  const columns = [
    { key: "name", label: "Nome" },
    { key: "is_kit", label: "É Kit" },
  ];

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Tipos ---
  useEffect(() => {
    if (isErrorTypes) {
      console.error("TypePage: Erro ao carregar tipos (useQuery):", typesError);
      showNotification(
        "error",
        `Erro ao carregar tipos: ${
          typesError.response?.data?.message || typesError.message
        }`,
      );
    }
  }, [isErrorTypes, typesError, showNotification]);

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // Lógica de Aplicação e Limpeza de Filtros (via useGenericFilter)
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues);
      applyUrlFilters(transformed); // Usa o setFilters do useUrlFilters
    },
    [applyUrlFilters],
  );

  const handleFilterClear = useCallback(
    (initialLocalFilterState) => {
      const transformedInitial = transformFiltersForUrl(
        initialLocalFilterState,
      );
      applyUrlFilters(transformedInitial); // Usa o setFilters do useUrlFilters
    },
    [applyUrlFilters],
  );

  // Inicializa o useGenericFilter com o estado inicial dos filtros do formulário
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        is_kit: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // --- Nova mutação para ativar tipo ---
  const inactivateTypeMutation = useMutation({
    mutationFn: async (typeId) => {
      showLoader();
      const response = await baseServiceDelete(entities.types.delete(typeId));
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        refreshList();
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      hideLoader();
    },
    onSettled: () => {
      handleCancelConfirmation();
    },
  });

  const activateTypeMutation = inactivateTypeMutation;

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        id: "viewGroups",
        icon: faLayerGroup,
        title: "Ver Grupos do Tipo",
        buttonClass: "btn-info",
        permission: "Listar grupos de tipos",
        onClick: (type) =>
          navigate(`/almoxarifado/produtos/tipos/associar-grupos/${type.id}`),
        show: (type) => !type.isDeleted && canAccess("Listar grupos de tipos"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar tipos",
        onClick: (type) =>
          navigate(`/almoxarifado/produtos/tipos/editar/${type.id}`),
        show: (type) =>
          !type.isDeleted &&
          !type.is_in_product &&
          canAccess("Atualizar tipos"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir tipos",
        onClick: (type) =>
          showConfirmation(
            type,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactivateTypeMutation.mutateAsync(type.id);
              } catch (error) {
                console.log("Erro ao excluir tipo:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (type) =>
          !type.isDeleted && !type.is_in_product && canAccess("Excluir tipos"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir tipos",
        onClick: (type) =>
          showConfirmation(
            type,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateTypeMutation.mutateAsync(type.id);
              } catch (error) {
                console.log("Erro ao restaurar tipo:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (type) => type.isDeleted && canAccess("Excluir tipos"),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactivateTypeMutation,
      activateTypeMutation,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Tipos" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersTypes({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Tipos"
          buttonText="Cadastrar Tipo"
          buttonLink="/almoxarifado/produtos/tipos/criar"
          canAccess={canAccess}
          permission="Criar tipos"
        />

        <TableWithInactiveFilter
          data={types}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default TypePage;
